import { InputGroupInput } from 'components/Form/InputGroup/InputGroupInput';
import React from 'react';

interface DecimalInputProps {
    value: number | string | undefined;
    onChange: (value: number) => void;
    scale?: number; // Number of decimal places (default 3)
    placeholder?: string;
}

export const DecimalInput: React.FC<DecimalInputProps> = ({
    value,
    onChange,
    scale = 3,
    placeholder = '0.000'
}) => {
    const [displayValue, setDisplayValue] = React.useState<string>(() => {
        if (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            !isNaN(Number(value)) &&
            Number(value) > 0
        ) {
            return Number(value).toFixed(scale);
        }
        return '';
    });
    const [isFocused, setIsFocused] = React.useState(false);

    // Format text. If the raw input contains an explicit decimal separator,
    // honor it and only enforce the decimal-place cap. Otherwise fall back to
    // right-to-left filling so single-keystroke entry still grows from the
    // smallest decimal place.
    const formatText = (rawValue: string): string => {
        const trimmed = rawValue.trim();

        if (/[.,]/.test(trimmed)) {
            const normalized = trimmed.replace(/,/g, '.');
            const cleaned = normalized.replace(/[^\d.]/g, '');
            // Keep only the first dot
            const firstDotIndex = cleaned.indexOf('.');
            const integerPart =
                firstDotIndex === -1
                    ? cleaned
                    : cleaned.slice(0, firstDotIndex).replace(/^0+(?=\d)/, '');
            const decimalPart =
                firstDotIndex === -1
                    ? ''
                    : cleaned
                          .slice(firstDotIndex + 1)
                          .replace(/\./g, '')
                          .slice(0, Math.max(scale, 0));
            const safeInteger = integerPart === '' ? '0' : integerPart;
            return scale > 0 ? `${safeInteger}.${decimalPart.padEnd(scale, '0')}` : safeInteger;
        }

        const cleanValue = trimmed.replace(/\D/g, '').replace(/^0+/, '');

        if (cleanValue.length === 0) {
            return scale > 0 ? `0.${'0'.repeat(scale)}` : '0';
        }

        if (scale === 0) {
            return cleanValue;
        }

        // For small values (1-2 digits), treat as decimals from the right
        if (cleanValue.length <= scale && cleanValue.length <= 2) {
            return `0.${cleanValue.padStart(scale, '0')}`;
        }

        // For larger values, split into integer and decimal parts
        const integerPart = cleanValue.slice(0, Math.max(cleanValue.length - scale, 0));
        const decimalPart = cleanValue.slice(Math.max(cleanValue.length - scale, 0));
        return `${integerPart || '0'}.${decimalPart.padStart(scale, '0')}`;
    };

    const commitDigits = (digits: string) => {
        const cleaned = digits.replace(/^0+/, '');

        if (cleaned.length === 0) {
            setDisplayValue('');
            onChange(NaN);
            return;
        }

        let formatted: string;
        if (scale === 0) {
            formatted = cleaned;
        } else if (cleaned.length <= scale) {
            formatted = `0.${cleaned.padStart(scale, '0')}`;
        } else {
            const integerPart = cleaned.slice(0, cleaned.length - scale);
            const decimalPart = cleaned.slice(cleaned.length - scale);
            formatted = `${integerPart}.${decimalPart}`;
        }

        setDisplayValue(formatted);
        const numericValue = parseFloat(formatted);
        onChange(isNaN(numericValue) ? NaN : numericValue);
    };

    // Right-to-left digit accumulator: each digit press shifts existing digits
    // toward higher places, so 1, 2, 3 produce 0.001 → 0.012 → 0.123 instead of
    // the input's default left-to-right edit at the caret.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;

        const currentDigits = displayValue.replace(/\D/g, '');

        if (/^[0-9]$/.test(event.key)) {
            event.preventDefault();
            commitDigits(currentDigits + event.key);
            return;
        }

        if (event.key === 'Backspace') {
            event.preventDefault();
            commitDigits(currentDigits.slice(0, -1));
            return;
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        // If empty, keep it empty to show placeholder
        if (newValue === '' || newValue.replace(/\D/g, '') === '') {
            setDisplayValue('');
            onChange(NaN);
            return;
        }

        const formatted = formatText(newValue);
        setDisplayValue(formatted);

        // Parse and send numeric value
        const numericValue = parseFloat(formatted);
        onChange(isNaN(numericValue) ? NaN : numericValue);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);

        // If empty, keep it empty
        if (displayValue === '' || displayValue.replace(/\D/g, '') === '') {
            setDisplayValue('');
            onChange(NaN);
            return;
        }

        // Ensure proper formatting on blur
        const formatted = formatText(displayValue);
        setDisplayValue(formatted);
        const numericValue = parseFloat(formatted);
        onChange(isNaN(numericValue) ? NaN : numericValue);
    };

    // Sync with external value changes (when not focused)
    React.useEffect(() => {
        if (!isFocused) {
            if (
                value !== undefined &&
                value !== null &&
                value !== '' &&
                !isNaN(Number(value)) &&
                Number(value) > 0
            ) {
                setDisplayValue(Number(value).toFixed(scale));
            } else {
                // If value is NaN, undefined, null, empty, or 0, show placeholder
                setDisplayValue('');
            }
        }
    }, [value, isFocused, scale]);

    return (
        <InputGroupInput
            type="text"
            inputMode="numeric"
            placeholder={placeholder}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
};
